import format from 'date-fns/format';
import { parseISO, getDaysInMonth, getYear, getMonth } from 'date-fns';
import { DateTime } from 'luxon';
import { isPresent } from '../object';
import {
  MONTHS,
  MonthType,
  WEEK_DAYS,
  WeekDayType,
} from 'Shared/constants/date';

export const formatDateShortUTC = (dateStr: string): string => {
  return DateTime.fromISO(dateStr, { zone: 'utc' }).toFormat('MM/dd/yyyy');
};

export const formatDateShortYearUTC = (dateStr: string): string => {
  return DateTime.fromISO(dateStr, { zone: 'utc' }).toFormat('MM/dd/yy');
};

/**
 * Formats a date string to MM/YY format
 * Handles multiple input formats: YYYY-MM, ISO dates, MM/DD/YYYY, MM/DD/YY, and MM/YY
 * @param dateStr - Date string in various formats
 * @returns Formatted date string in MM/YY format (e.g., "10/26")
 */
export const formatDateMonthYear = (
  dateStr: string | undefined | null
): string => {
  if (!dateStr) return '';

  try {
    // Check if date is already in MM/YY format (e.g., "10/26")
    const mmyyPattern = /^\d{1,2}\/\d{2}$/;
    if (mmyyPattern.test(dateStr)) {
      const [month, year] = dateStr.split('/');
      return `${month.padStart(2, '0')}/${year}`;
    }

    // Check if date is in YYYY-MM format (e.g., "2026-10")
    const yearMonthPattern = /^\d{4}-\d{2}$/;
    if (yearMonthPattern.test(dateStr)) {
      const [year, month] = dateStr.split('-');
      const twoDigitYear = year.slice(-2);
      return `${month}/${twoDigitYear}`;
    }

    // Check if date is in MM/DD/YYYY format (e.g., "10/26/2026")
    const mmddyyyyPattern = /^\d{1,2}\/\d{1,2}\/\d{4}$/;
    if (mmddyyyyPattern.test(dateStr)) {
      const [month, , year] = dateStr.split('/');
      const twoDigitYear = year.slice(-2);
      return `${month.padStart(2, '0')}/${twoDigitYear}`;
    }

    // Check if date is in MM/DD/YY format (e.g., "10/26/26")
    const mmddyyPattern = /^\d{1,2}\/\d{1,2}\/\d{2}$/;
    if (mmddyyPattern.test(dateStr)) {
      const [month, , year] = dateStr.split('/');
      return `${month.padStart(2, '0')}/${year}`;
    }

    // Handle ISO format dates (e.g., "2026-07-30T00:00:00.000+00:00")
    // Extract date part before timezone conversion to preserve the original month/year
    // This is important for insurance prior policy dates where we want to display
    // the date as recorded, not as it would appear in UTC
    if (dateStr.includes('T')) {
      const datePart = dateStr.split('T')[0]; // "2026-07-30"
      const [year, month] = datePart.split('-');
      if (year && month && /^\d{4}$/.test(year) && /^\d{2}$/.test(month)) {
        const twoDigitYear = year.slice(-2);
        return `${month}/${twoDigitYear}`;
      }
    }

    // Fallback: Try parsing as ISO date with timezone conversion
    const dateTime = DateTime.fromISO(dateStr, { zone: 'utc' });
    if (dateTime.isValid) {
      return dateTime.toFormat('MM/yy');
    }

    // If all parsing fails, return the original string
    return dateStr;
  } catch (error) {
    return dateStr;
  }
};

export const formatDateShort = (date: string): string => {
  return _format(date, 'MM/dd/yyyy');
};

export const formatDateHyphenated = (date: string): string => {
  return _format(date, 'yyyy-MM-dd');
};

export const formatDateMiddle = (date: string): string => {
  return _format(date, 'MMM dd, yyyy');
};

/**
 * Formats a date in the format of MM/dd/yyyy t using the provided timezone
 * i.e. 04/03/2023 2:50 AM
 * @param date
 * @param zone
 * @returns
 */
export const formatDateWithTime = (date: string, zone: string): string => {
  return isPresent(zone)
    ? DateTime.fromISO(date, { zone }).toFormat('MM/dd/yyyy t')
    : date;
};

/**
 * Formats a date in the format of MM/dd/yyyy tt using the provided timezone
 * i.e. 04/03/2023 2:50:25 AM
 * @param date
 * @param zone
 * @returns
 */
export const formatDateTimeFullUsingTimezone = (
  date: string,
  zone?: string
): string => {
  return isPresent(zone)
    ? DateTime.fromISO(date, { zone }).toFormat('MM/dd/yyyy tt')
    : date;
};

/**
 * Formats a date in the format of MM/dd/yyyy tt ZZZZ using the provided timezone
 * i.e. 04/03/2023 2:50:25 AM
 * @param date
 * @param zone
 * @returns
 */
export const formatDateTimeFullWithAbbreviatedTimezone = (
  date: string,
  zone?: string
): string => {
  return isPresent(zone)
    ? DateTime.fromISO(date, { zone }).toFormat('MM/dd/yyyy t ZZZZ')
    : date;
};

/**
 * Formats a date in the format of MM/dd/yyyy 'at' ttt using the provided timezone
 * i.e. 04/03/2023 at 2:50:25 AM EDT
 * @param date
 * @param timeZone
 * @returns formatted date
 */
export const formatDateShortWithAt = (
  date: string,
  timeZone: string | undefined
) => {
  return DateTime.fromISO(date, {
    zone: timeZone,
  }).toFormat("MM/dd/yyyy 'at' ttt");
};

/**
 * Formats a date in the format of MMM dd, yyyy  using the provided timezone.
 * i.e. Apr 03, 2023
 * @param date
 * @param zone
 * @returns formatted date
 */
export const formatDateTimeMiddleUsingTimezone = (
  date: string,
  zone?: string
): string => {
  return isPresent(zone)
    ? DateTime.fromISO(date, { zone }).toFormat('MMM dd, yyyy')
    : date;
};

/**
 * Formats a date in the format of MM/dd/yyyy using the provided timezone.
 * i.e. 04/03/2023
 * @param date
 * @param zone
 * @returns format date
 */
export const formatDateTimeShortUsingTimezone = (
  date: string,
  zone?: string
): string => {
  return isPresent(zone)
    ? DateTime.fromISO(date, { zone }).toFormat('MM/dd/yyyy')
    : date;
};

/**
 * Formats a date in the format of MM/dd/yyyy ZZZZ using the provided timezone.
 * i.e. 04/03/2023 EDT
 * @param date
 * @param zone
 * @returns format date
 */
export const formatDateTimeShortWithAbbreviatedTimezone = (
  date: string,
  zone?: string
): string => {
  return isPresent(zone)
    ? DateTime.fromISO(date, { zone }).toFormat('MM/dd/yyyy ZZZZ')
    : date;
};

const isUTCFormatted = (date: string) =>
  date.slice(-1) === 'Z' || date.slice(-6).match('[+-][0-9]{2}:[0-9]{2}');

export const formatDateFromUTCShort = (date: string, zone?: string): string => {
  return formatDateTimeShortUsingTimezone(
    isUTCFormatted(date) ? date : date + 'Z',
    zone
  );
};

const _format = (date: string, dateFormat: string) => {
  try {
    const utcDate = parseISO(date);
    return format(utcDate, dateFormat);
  } catch {
    return 'Invalid Date';
  }
};

export const monthDateYearNormalizer = (value: string): string => {
  if (!value) return value;

  const digits = Array.from(value).filter(char => /\d/.test(char));

  const splitValues = value.split('/');

  // check for pasted values and iOS autofill
  // if 3 chunks are present and its a valid year
  // map month and date to require 2 characters
  if (splitValues.length === 3 && splitValues[2].length === 4) {
    return splitValues.join('/');
  }

  return [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)]
    .filter(chunk => chunk.length > 0)
    .map(chunk => chunk.join(''))
    .join('/');
};

/**
 * Normalizes date input for MM/DD/YY format (2-digit year)
 * @param value - Raw input value
 * @param allowFullYear - If true, allows 4-digit years without converting to 2-digit
 * @returns Formatted date string in MM/DD/YY or MM/DD/YYYY format
 */
export const monthDateYearNormalizerShort = (
  value: string,
  allowFullYear: boolean = false
): string => {
  if (!value) return value;

  const digits = Array.from(value).filter(char => /\d/.test(char));

  const splitValues = value.split('/');

  // check for pasted values and iOS autofill
  // if 3 chunks are present and its a valid 2-digit year
  if (splitValues.length === 3 && splitValues[2].length === 2) {
    return splitValues.join('/');
  }

  // Handle 4-digit year - either keep it or convert to 2-digit based on allowFullYear
  if (splitValues.length === 3 && splitValues[2].length === 4) {
    if (allowFullYear) {
      return splitValues.join('/');
    }
    const year = splitValues[2].slice(-2); // Take last 2 digits
    return `${splitValues[0]}/${splitValues[1]}/${year}`;
  }

  // Determine max year digits based on allowFullYear
  const maxYearDigits = allowFullYear ? 8 : 6;

  return [
    digits.slice(0, 2),
    digits.slice(2, 4),
    digits.slice(4, maxYearDigits),
  ]
    .filter(chunk => chunk.length > 0)
    .map(chunk => chunk.join(''))
    .join('/');
};

export const sortByDate: <T extends { [key: string]: any }, K extends keyof T>(
  direction: 'none' | 'asc' | 'desc',
  property: K
) => (item: T, item2: T) => number = (direction, property) => (item, item2) => {
  const dateDiff = DateTime.fromISO(item[property]).diff(
    DateTime.fromISO(item2[property])
  ).milliseconds;

  if (dateDiff === 0) return 0;

  const sortDirectionMap = {
    asc: dateDiff > 0 ? 1 : -1,
    desc: dateDiff > 0 ? -1 : 1,
    none: 0,
  };

  return sortDirectionMap[direction];
};

/**
 * This gets the JS Date for a specific time in the time
 * zone given, ignoring the time zone the user is in.
 * @param date date to convert in ISO string format
 * @param timeZone time zone to use for conversion
 * @returns The converted date
 */
export const convertDateIgnoringLocalTimeZone = (
  date: string,
  timeZone?: string
): Date => {
  const localTimezoneOffset = DateTime.local().offset;
  const desiredTimezoneOffset = DateTime.fromObject({
    zone: timeZone,
  }).offset;

  const offsetInMinutes = localTimezoneOffset - desiredTimezoneOffset;
  return DateTime.fromISO(date)
    .minus({ minutes: offsetInMinutes })
    .toJSDate();
};

export const getSalutation = () => {
  const time = new Date().getHours();

  return time < 12
    ? 'morning'
    : time > 11 && time < 15
    ? 'afternoon'
    : 'evening';
};

export const getMonthNumberFromName = (monthName: string) => {
  const date = new Date(`${monthName} 1, 2022`);

  return date.getMonth() + 1;
};

export const calcualteDaysInMonth = (
  month: string | null,
  year: string | null
) => {
  const targetYear = year || getYear(new Date());
  const targetMonth = month
    ? getMonthNumberFromName(month)
    : getMonth(new Date());

  const targetDate = `${targetYear}-${targetMonth}-10`;

  return getDaysInMonth(new Date(targetDate));
};

export const getMonthNameFromNumber = (monthNumber: number | string) => {
  const targetYear = getYear(new Date());
  const targetDate = `${targetYear} ${monthNumber} 10`;
  const formattedDate = format(new Date(targetDate), 'LLLL');

  return formattedDate;
};

export const addDaysToDate = (
  dateString: string,
  daysToAdd: number
): string | null => {
  const date = new Date(dateString);

  if (isNaN(date.getTime())) {
    return null;
  }

  // Add the number of days to the date
  date.setDate(date.getDate() + daysToAdd);

  return formatDateShort(date.toISOString());
};

export const getWeekDay = (date: Date): WeekDayType => {
  return WEEK_DAYS[date.getUTCDay()];
};

export function getMonthName(date: Date): MonthType {
  return MONTHS[date.getUTCMonth()];
}

export const formatDateForInput = (dateString: string | undefined): string => {
  if (!dateString) return '';
  return DateTime.fromISO(dateString).toFormat("yyyy-MM-dd'T'HH:mm");
};

/**
 * Robust date type detection - checks if a date string is likely from server (ISO format)
 * This replaces fragile includes('T') checks that can be fooled by user input
 * @param dateString - Date string to check
 * @returns true if the date appears to be in ISO/server format, false otherwise
 */
export const isServerDate = (
  dateString: string | null | undefined
): boolean => {
  if (!dateString) return false;

  const str = dateString.toString();

  // Check for ISO 8601 format patterns:
  // - Contains both date and time separators (- and :)
  // - Has timezone indicator (Z, +, or -)
  // - Matches common ISO patterns like YYYY-MM-DDTHH:mm:ss
  const isoPatterns = [
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/, // Basic ISO with T
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}/, // ISO with milliseconds
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.*[Z+-]/, // ISO with timezone
  ];

  return isoPatterns.some(pattern => pattern.test(str));
};

/**
 * Parses a 2-digit year using business-appropriate sliding window logic
 * Designed for insurance policies - allows reasonable future dates while handling historical dates
 * @param twoDigitYear - 2-digit year (0-99)
 * @returns 4-digit year
 */
export const parse2DigitYear = (twoDigitYear: number): number => {
  const currentYear = new Date().getFullYear();
  const currentCentury = Math.floor(currentYear / 100) * 100;
  const currentYearInCentury = currentYear % 100;

  // Use a sliding window that allows reasonable future dates
  // If the 2-digit year is more than 10 years in the future, assume previous century
  // This allows for policy end dates that might be a few years in the future
  if (twoDigitYear > currentYearInCentury + 10) {
    return twoDigitYear + currentCentury - 100; // Previous century
  } else {
    return twoDigitYear + currentCentury; // Current century
  }
};

/**
 * Parses a date string in MM/dd/yy format to a JavaScript Date object
 * Uses consistent business logic for 2-digit year parsing
 * @param dateString - Date string in MM/dd/yy format (e.g., "07/30/26")
 * @returns JavaScript Date object or null if invalid
 */
export const parseDateSlashFormatShort = (
  dateString: string | undefined
): Date | null => {
  if (!dateString) return null;

  try {
    // Split the date string to extract components
    const parts = dateString.split('/');
    if (parts.length !== 3) return null;

    const month = parseInt(parts[0], 10);
    const day = parseInt(parts[1], 10);
    const yearInput = parseInt(parts[2], 10);

    // Validate basic ranges
    if (month < 1 || month > 12 || day < 1 || day > 31) {
      return null;
    }

    // Parse the year using consistent business logic
    const year = yearInput < 100 ? parse2DigitYear(yearInput) : yearInput;

    // Create and validate the date in UTC to be consistent with API transformation
    const parsedDate = new Date(Date.UTC(year, month - 1, day)); // month is 0-indexed in Date constructor

    // Verify the date is actually valid (handles leap years, etc.)
    if (
      parsedDate.getUTCFullYear() !== year ||
      parsedDate.getUTCMonth() !== month - 1 ||
      parsedDate.getUTCDate() !== day
    ) {
      return null;
    }

    return parsedDate;
  } catch {
    return null;
  }
};

/**
 * Parses a date string in MM/dd/yy format and converts it to ISO string with UTC timezone
 * Designed for prior insurance policies - assumes 2-digit years represent dates within the last 100 years
 * @param dateString - Date string in MM/dd/yy format (e.g., "07/30/26")
 * @returns ISO string with UTC timezone (e.g., "2026-07-30T00:00:00.000+00:00") or empty string if invalid
 */
export const parseDateSlashFormatShortToISO = (
  dateString: string | undefined
): string => {
  if (!dateString) return '';

  const parsedDate = parseDateSlashFormatShort(dateString);
  if (!parsedDate) return '';

  try {
    // Convert to UTC DateTime and return ISO string
    // Note: Removed restrictive business validation for prior policies
    // Historical policies may have had future end dates when originally issued
    const utcDate = DateTime.utc(
      parsedDate.getUTCFullYear(),
      parsedDate.getUTCMonth() + 1, // DateTime months are 1-indexed
      parsedDate.getUTCDate(),
      0,
      0,
      0,
      0
    );

    return utcDate.toISO() || '';
  } catch {
    return '';
  }
};

/**
 * Format a Date object to YYYY-MM-DD in local timezone
 * Avoids UTC conversion issues by using local date methods
 * @param date - JavaScript Date object
 * @returns Date string in YYYY-MM-DD format (e.g., "2025-11-25")
 */
export const formatDateToLocalISO = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Parse YYYY-MM-DD string to Date object in local timezone
 * Avoids UTC conversion issues by explicitly using local date constructor
 * @param dateString - Date string in YYYY-MM-DD format (e.g., "2025-11-25")
 * @returns JavaScript Date object in local timezone
 */
export const parseLocalISODate = (dateString: string): Date => {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};
